<template>
  <article
    v-if="channel"
    class="list-item__card"
    :data-placeholder="placeholder"
    :data-highlight="shouldShowHighlight"
    :data-dd-action-name="`sidenav_list_item_compact_${channel.id}`"
    @click="goToPillars"
  >
    <header
      class="list-item__header"
      :data-dd-action-name="`sidenav_list_item_compact_${channel.id}`"
    >
      <CChannelHighlight
        v-if="channel.staffArray.length"
        tooltip-position="top-start"
        size="small"
        :channel-id="channel.id"
        :is-highlighted="channel.highlight"
      />

      <WParagraph class="list-item__member">
        {{ member && member.fullName }}
      </WParagraph>

      <div class="list-item__info">
        <CNotificationsGroup
          :is-tab="false"
          :counters="staffNotifications"
          show-mentions
        />
        <WHelper v-if="dateField" class="list-item__time-message">
          {{ dateField }}
        </WHelper>
        <CChannelSla
          v-if="showSla && channel.isWaiting"
          class="list-item__sla"
          :channel="channel"
          tooltip-position="top-end"
        />
      </div>
    </header>
    <div class="list-item__content">
      <CPreviewableMessage
        :staffs="channel.staffs"
        :previewable-messages="channel.previewableMessages"
        :last-previewable-message="channel.lastPreviewableMessage"
        hide-member-name
        multiple-lines
      />
    </div>
    <footer class="list-item__footer">
      <WParagraph v-if="channel.name" class="list-item__extra-info">
        {{ channel.name }}
      </WParagraph>

      <CAssessmentIcon v-else-if="showAssessment" :assessment="assessment" />

      <WParagraph v-else class="list-item__extra-info list-item__tag">
        {{ chatLabel }}
      </WParagraph>

      <CChannelTag
        v-if="isChannelArchived"
        class="list-item__left-info"
        :tag="archivedChannelTag"
      />

      <div v-else class="list-item__container-tag">
        <PMemberTypeTag
          v-for="tag in member.tags"
          :key="tag.type"
          class="list-item__left-info"
          :tag="tag"
        />

        <CChannelTag
          v-if="isFirstAccessAndScreeningNurse"
          class="list-item__left-info"
          :tag="firstAccessChannelTag"
        />
      </div>

      <CHealthcareTeam
        v-if="showStaff"
        limit
        class="list-item__left-info"
        tooltip-position="bottom-end"
        layout="names"
        :team="channelStaffTeam"
      />
      <WLabel
        v-if="showWaitingForN2"
        class="list-item__waiting-n2"
        mode="warning"
      >
        Aguardando N2
      </WLabel>
    </footer>
  </article>
</template>

<script>
import { WHelper, WLabel, WParagraph } from '@alice-health/wonderland-vue'
import { mapGetters, mapState } from 'vuex'

import {
  CAssessmentIcon,
  CChannelSla,
  CChannelTag,
  CHealthcareTeam,
  CNotificationsGroup
} from '@/components/channels/atoms'
import {
  CChannelHighlight,
  CPreviewableMessage
} from '@/components/channels/molecules'
import { PMemberTypeTag } from '@/components/pillars/atoms'
import {
  CHANNEL_CATEGORIES,
  CHANNEL_KINDS,
  ChannelStatus,
  CHAT_TITLE
} from '@/models/channels'
import { ARCHIVED_CHANNEL_TAG, FIRST_ACCESS_CHANNEL_TAG } from '@/models/tags'
import { UserRole } from '@/modules/user'
import { timeDifference } from '@/utils/date'

export default {
  components: {
    CPreviewableMessage,
    CChannelSla,
    CChannelHighlight,
    CHealthcareTeam,
    CNotificationsGroup,
    CChannelTag,
    CAssessmentIcon,
    PMemberTypeTag,
    WParagraph,
    WHelper,
    WLabel
  },
  props: {
    channel: { type: Object, required: true },
    isEmergency: Boolean
  },
  data() {
    return {
      archivedChannelTag: ARCHIVED_CHANNEL_TAG,
      firstAccessChannelTag: FIRST_ACCESS_CHANNEL_TAG
    }
  },
  computed: {
    ...mapState('channel/drawer', ['currentSortBy']),
    ...mapState('ui/channelBar', ['menuState']),
    ...mapGetters('user', ['user']),
    ...mapGetters('member', ['getMemberById']),
    dateField() {
      let dateField = ''
      if (this.showSla && this.channel.isWaiting) {
        dateField = this.channel.waitingSince?.seconds
      } else if (!this.showSla && this.channel.timeLastMessage) {
        dateField = this.channel.timeLastMessage?.seconds
      }

      return dateField ? timeDifference(dateField) : ''
    },
    showSla() {
      return this.currentSortBy.type.includes('waiting-since')
    },
    isChannelArchived() {
      return this.channel?.status === ChannelStatus.ARCHIVED
    },
    isFirstAccessAndScreeningNurse() {
      return (
        this.channel?.tags.includes(FIRST_ACCESS_CHANNEL_TAG.value) &&
        this.user.role === UserRole.SCREENING_NURSE
      )
    },
    member() {
      const member = this.getMemberById({
        channelPersonId: this.channel?.channelPersonId
      })
      return { ...member }
    },
    placeholder() {
      return !this.member.id ? true : null
    },
    channelStaffTeam() {
      if (this.channel?.staffArray.length) {
        return this.channel?.staffArray
      }

      return []
    },
    staffNotifications() {
      const { unreadMessages, privateUnreadMessages, privateUnreadMentions } =
        this.channel
      return {
        unreadMessages,
        privateUnreadMessages,
        privateUnreadMentions
      }
    },
    assessment() {
      return this.channel?.assessment || this.channel?.screening?.assessment
    },
    isChat() {
      return this.channel?.kind === CHANNEL_KINDS.CHAT
    },
    showAssessment() {
      return this.assessment && this.isChat
    },
    showStaff() {
      return (
        this.channel?.status !== ChannelStatus.ARCHIVED &&
        !this.showWaitingForN2
      )
    },
    showWaitingForN2() {
      return this.channel?.tags.includes('aguardando_n2')
    },
    showHighlight() {
      return (
        !this.channel?.category ||
        (this.channel?.category === CHANNEL_CATEGORIES.ASSISTANCE &&
          !(this.channel?.demands && this.channel?.demands.length))
      ).toString()
    },
    shouldShowHighlight() {
      return this.showHighlight || null
    },
    chatLabel() {
      if (this.isChat && this.channel.tags?.length) {
        return this.channel.tags[0].replaceAll('_', ' ')
      }
      return CHAT_TITLE
    },
    isLoggedStaffNotInCurrentChannel() {
      const currentStaffId = this.user.id
      return !this.channel?.staff?.[currentStaffId]
    }
  },
  methods: {
    addStaffToChannel() {
      return this.$store.dispatch('pillars/channel/addStaffToChannel', {
        channelId: this.channel.id,
        staffId: this.user.id
      })
    },
    async goToPillars(e) {
      this.$store.commit('ui/channel/resetChannelsUI')
      if (this.$route.query.channelId === this.channel.id) {
        if (this.isLoggedStaffNotInCurrentChannel) {
          await this.addStaffToChannel()
        }

        return
      }
      const personId = this.channel.personId || this.member.id
      const routerPath = `/member/${personId}/pillars?channelId=${this.channel.id}`

      let shouldGoToChannel = true

      if (this.isEmergency) {
        const result = await this.addStaffToChannel()
        if (!result) {
          shouldGoToChannel = false
        }
      }

      if (shouldGoToChannel) {
        if (e.ctrlKey || e.metaKey) {
          window.open(routerPath, '_blank')
          return
        }

        this.$router.push(routerPath)
      }
    }
  }
}
</script>

<style lang="scss">
.list-item {
  &__card {
    gap: var(--gl-spacing-02);
    padding: var(--gl-spacing-04);
    border-radius: var(--gl-border-radius-sm);
    background-color: var(--gl-color-shades-white);
    color: var(--sys-color-content-primary);
    cursor: pointer;
    position: relative;

    &[data-placeholder] {
      .list-item__member,
      .list-item__message {
        width: 30%;
        height: 16px;
        background: var(--gl-color-shades-gray-20);
        border-radius: var(--gl-border-radius-xs);
      }

      .list-item__message {
        width: 100%;
        margin-top: var(--gl-spacing-02);
      }
    }

    &:hover {
      box-shadow: var(--gl-shadow-level-04);
    }

    & + & {
      margin-top: var(--gl-spacing-04);
    }

    p {
      margin: 0;
    }

    &[data-highlight='true']:before {
      content: '';
      position: absolute;
      width: 4px;
      height: 100%;
      left: 0;
      top: 0;
      background: var(--gl-color-shades-yellow-60);
      border-radius: var(--gl-border-radius-xs) 0 0 var(--gl-border-radius-xs);
    }
  }

  &__header {
    display: flex;
    align-items: center;
    gap: var(--gl-spacing-02);
  }

  &__member {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__info {
    display: flex;
    align-items: center;
    margin-left: auto;
  }

  &__time-message {
    margin: 0 var(--gl-spacing-02);
  }

  &__sla {
    margin: 0;
  }

  &__content {
    margin: var(--gl-spacing-02) 0;
  }

  &__footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__extra-info {
    color: var(--gl-color-shades-gray-60);
  }

  &__left-info {
    margin-left: auto;
  }

  &__waiting-n2 {
    margin-left: auto;
  }

  &__tag:first-letter {
    text-transform: uppercase;
  }

  &__container-tag {
    display: flex;

    .list-item__left-info {
      margin-left: var(--gl-spacing-02);
    }

    .list-item__left-info.h-tag {
      padding: 0 var(--gl-spacing-02);
    }
  }
}
</style>
